import type {
  DecisionEngineChannelConfig,
  DecisionEngineChannelType,
} from "@hightouch/lib/customer-data/decision-engine/types";
import type { DecisionEngineDestinationDefinition } from "./definition";
import { accDefinition } from "./adobe-campaign-classic";
import { ajoDefinition } from "./adobe-experience-platform";
import { airshipDefinition } from "./airship";
import { attentiveDefinition } from "./attentive";
import { brazeCanvasDefinition, brazeTriggerDefinition } from "./braze";
import { cordialDefinition } from "./cordial";
import { eloquaCdoDefinition, eloquaTriggerDefinition } from "./eloqua";
import { iterableDefinition } from "./iterable";
import { klaviyoDefinition } from "./klaviyo";
import { marketoDefinition } from "./marketo";
import { moengageDefinition } from "./moengage";
import { mongodbDefinition } from "./mongodb";
import { oneSignalDefinition } from "./one-signal";
import { personalizationApiDefinition } from "./personalization-api";
import {
  sfmcDataExtensionsDefinition,
  sfmcJourneyDefinition,
  sfmcTriggerDefinition,
} from "./sfmc";

const DESTINATION_REGISTRY: Record<
  string,
  DecisionEngineDestinationDefinition
> = {
  // Trigger
  iterable: iterableDefinition,
  "iterable:template": iterableDefinition,
  "iterable:campaign": iterableDefinition,
  braze: brazeTriggerDefinition,
  "braze:trigger": brazeTriggerDefinition,
  "braze:canvas": brazeCanvasDefinition,
  sfmc: sfmcTriggerDefinition,
  "sfmc:trigger": sfmcTriggerDefinition,
  eloqua: eloquaTriggerDefinition,
  "eloqua:trigger": eloquaTriggerDefinition,

  cordial: cordialDefinition,
  marketo: marketoDefinition,
  onesignal: oneSignalDefinition,
  "adobe-campaign-classic": accDefinition,
  airship: airshipDefinition,

  // Event
  klaviyo: klaviyoDefinition,
  attentive: attentiveDefinition,
  moengage: moengageDefinition,
  "adobe-experience-platform": ajoDefinition,
  "sfmc:journeyEntryEvent": sfmcJourneyDefinition,
  "eloqua:customDataObject": eloquaCdoDefinition,

  // Batch
  "sfmc:dataExtensions": sfmcDataExtensionsDefinition,

  // Onsite
  "hightouch-personalization-api": personalizationApiDefinition,

  // Custom passthrough — dumps the AID row as-is; downstream system handles transforms.
  mongodb: mongodbDefinition,
};

function definitionKey(
  destinationType: string,
  channelConfig?: DecisionEngineChannelConfig,
): string {
  return channelConfig?.type
    ? `${destinationType}:${channelConfig.type}`
    : destinationType;
}

/**
 * Look up a destination definition by type and optional channel config.
 *
 * Tries `${destinationType}:${channelConfig.type}` first (e.g. "sfmc:trigger"),
 * then falls back to `${destinationType}` (e.g. "sfmc" -> trigger default).
 * Returns undefined if no definition is found.
 */
function findDestinationDefinition(
  destinationType: string,
  channelConfig?: DecisionEngineChannelConfig,
): DecisionEngineDestinationDefinition | undefined {
  return (
    DESTINATION_REGISTRY[definitionKey(destinationType, channelConfig)] ??
    DESTINATION_REGISTRY[destinationType]
  );
}

/**
 * Look up a destination definition by type and optional channel config.
 * Throws if no definition is found.
 */
export function getDestinationDefinition(
  destinationType: string,
  channelConfig?: DecisionEngineChannelConfig,
): DecisionEngineDestinationDefinition {
  const definition = findDestinationDefinition(destinationType, channelConfig);
  if (!definition) {
    throw new Error(
      `Unknown decision engine destination type: ${definitionKey(destinationType, channelConfig)}`,
    );
  }
  return definition;
}

/**
 * Whether a channel's sync is enrollment-shaped (see
 * `DecisionEngineDestinationDefinition.enrollmentChannelTypes`).
 */
export function isEnrollmentChannel(
  destinationType: string,
  channelType: string,
  channelConfig?: DecisionEngineChannelConfig,
): boolean {
  return (
    findDestinationDefinition(
      destinationType,
      channelConfig,
    )?.enrollmentChannelTypes?.includes(
      channelType as DecisionEngineChannelType,
    ) ?? false
  );
}
